import { useSyncExternalStore } from "react";

/**
 * Small, in-memory and privacy-safe performance event stream for Benchmark.
 * Events are available through getBenchmarkPerformanceEvents(), the
 * `window.__ALTA_BENCHMARK_PERFORMANCE__.getEvents()` baseline hook, and the
 * `alta:benchmark-performance` CustomEvent. No storage/network exporter.
 */
export type BenchmarkPerformanceEvent =
  | {
      event: "visit_start";
      navigation_id: string;
      navigation_kind: "direct" | "spa";
      at_ms: number;
    }
  | {
      event: "request_attempt";
      navigation_id: string;
      attempt: number;
      duration_ms: number;
      outcome: "success" | "http_error" | "network_error" | "cancelled";
      status: number | null;
      request_id: string | null;
    }
  | {
      event: "cache_state";
      navigation_id: string;
      surface: "hit" | "miss";
      pinned_snapshot: "available" | "pending" | "unavailable";
    }
  | {
      event: "first_visible_tile";
      navigation_id: string;
      ttfk_ms: number;
    }
  | {
      event: "visit_end";
      navigation_id: string;
      outcome: "visible" | "failed" | "incomplete" | "navigated";
    };

declare global {
  interface Window {
    __ALTA_BENCHMARK_PERFORMANCE__?: {
      getEvents: () => BenchmarkPerformanceEvent[];
    };
  }
}

export interface BenchmarkVisit {
  navigationId: string;
  navigationKind: "direct" | "spa";
  startedAt: number;
}

const MAX_EVENTS = 200;
const SAFE_SERVER_REQUEST_ID = /^[A-Za-z0-9_-]{16}$/;
const events: BenchmarkPerformanceEvent[] = [];
const attemptCounts = new Map<string, number>();
const visitListeners = new Set<() => void>();
let activeVisit: BenchmarkVisit | null = null;
let observedPath: string | null = null;
let visibleVisitId: string | null = null;
let endedVisitId: string | null = null;
let failedVisitId: string | null = null;
let cacheStateVisitId: string | null = null;
let capturedNavigationStart: { pathname: string; at: number } | null = null;
const initialBrowserPath = typeof window !== "undefined" ? window.location.pathname : null;

function monotonicNow(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

function notifyVisitChange(): void {
  // Router transitions can keep the previous page mounted while another lazy
  // route suspends. Notify after render, including when no remount occurs.
  queueMicrotask(() => visitListeners.forEach((listener) => listener()));
}

function subscribeVisit(listener: () => void): () => void {
  visitListeners.add(listener);
  return () => { visitListeners.delete(listener); };
}

export function useBenchmarkVisit(): BenchmarkVisit | null {
  return useSyncExternalStore(subscribeVisit, getActiveBenchmarkVisit, () => null);
}

function randomNavigationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function emit(event: BenchmarkPerformanceEvent): void {
  events.push(event);
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function"
    && typeof CustomEvent !== "undefined") {
    window.dispatchEvent(new CustomEvent("alta:benchmark-performance", { detail: event }));
  }
}

function endVisit(outcome: "visible" | "failed" | "incomplete" | "navigated"): void {
  if (!activeVisit || endedVisitId === activeVisit.navigationId) return;
  endedVisitId = activeVisit.navigationId;
  emit({ event: "visit_end", navigation_id: activeVisit.navigationId, outcome });
  activeVisit = null;
  notifyVisitChange();
}

/**
 * Called as the router resolves a location (before a lazy route renders), so
 * SPA navigation timing starts at the route change rather than component mount.
 */
export function observeBenchmarkRoute(pathname: string, at = monotonicNow()): void {
  if (observedPath === pathname) {
    if (capturedNavigationStart?.pathname === pathname) capturedNavigationStart = null;
    return;
  }
  const wasBenchmark = observedPath === "/benchmark";
  const isInitialLocation = observedPath === null;
  observedPath = pathname;
  if (capturedNavigationStart && capturedNavigationStart.pathname !== pathname) {
    capturedNavigationStart = null;
  }
  if (pathname === "/benchmark") {
    // A direct load's navigation start is the document navigation start
    // (performance.now() == 0); SPA visits start at the router transition.
    const isDirectLoad = isInitialLocation
      && (initialBrowserPath === null || initialBrowserPath === pathname);
    const capturedStart = capturedNavigationStart?.pathname === pathname
      ? capturedNavigationStart.at
      : null;
    const startedAt = isDirectLoad ? 0 : capturedStart ?? at;
    capturedNavigationStart = null;
    const visit: BenchmarkVisit = {
      navigationId: randomNavigationId(),
      navigationKind: isDirectLoad ? "direct" : "spa",
      startedAt,
    };
    activeVisit = visit;
    notifyVisitChange();
    visibleVisitId = null;
    endedVisitId = null;
    failedVisitId = null;
    cacheStateVisitId = null;
    attemptCounts.set(visit.navigationId, 0);
    while (attemptCounts.size > MAX_EVENTS) {
      const oldest = attemptCounts.keys().next().value;
      if (!oldest) break;
      attemptCounts.delete(oldest);
    }
    emit({
      event: "visit_start",
      navigation_id: visit.navigationId,
      navigation_kind: visit.navigationKind,
      at_ms: Math.max(0, at - startedAt),
    });
  } else if (wasBenchmark) {
    endVisit(
      visibleVisitId
        ? "visible"
        : failedVisitId === activeVisit?.navigationId
          ? "failed"
          : "navigated",
    );
  }
}

/** Captures real browser navigation intent before React loads the route chunk. */
export function captureBenchmarkNavigationStart(pathname: string, at = monotonicNow()): void {
  capturedNavigationStart = { pathname, at };
}

export function getActiveBenchmarkVisit(): BenchmarkVisit | null {
  return activeVisit;
}

export function isCurrentBenchmarkSelection(expected: string, current: string): boolean {
  return expected === current;
}

export function recordBenchmarkCacheState(
  navigationId: string,
  surfaceCached: boolean,
  pinState: "available" | "pending" | "unavailable",
): void {
  if (activeVisit?.navigationId !== navigationId || cacheStateVisitId === navigationId) return;
  cacheStateVisitId = navigationId;
  emit({
    event: "cache_state",
    navigation_id: navigationId,
    surface: surfaceCached ? "hit" : "miss",
    pinned_snapshot: pinState,
  });
}

export function recordBenchmarkFirstVisibleTile(navigationId: string, at = monotonicNow()): boolean {
  if (activeVisit?.navigationId !== navigationId || visibleVisitId === navigationId) return false;
  visibleVisitId = navigationId;
  emit({
    event: "first_visible_tile",
    navigation_id: navigationId,
    ttfk_ms: Math.max(0, at - activeVisit.startedAt),
  });
  return true;
}

export function markBenchmarkVisitFailed(navigationId: string): void {
  if (activeVisit?.navigationId === navigationId) failedVisitId = navigationId;
}

export function endBenchmarkVisit(
  navigationId: string,
  outcome: "visible" | "failed" | "incomplete" | "navigated",
): void {
  if (activeVisit?.navigationId !== navigationId) return;
  endVisit(outcome);
}

/** Called by the Axios interceptor once per real network attempt. */
export function startBenchmarkRequestAttempt(navigationId: string): number {
  if (!attemptCounts.has(navigationId)) return 0;
  const next = (attemptCounts.get(navigationId) ?? 0) + 1;
  attemptCounts.set(navigationId, next);
  while (attemptCounts.size > MAX_EVENTS) {
    const oldest = Array.from(attemptCounts.keys()).find((id) => id !== activeVisit?.navigationId);
    if (!oldest) break;
    attemptCounts.delete(oldest);
  }
  return next;
}

export function recordBenchmarkRequestAttempt(
  navigationId: string,
  attempt: number,
  durationMs: number,
  outcome: "success" | "http_error" | "network_error" | "cancelled",
  status: number | null,
  requestId: string | null,
): void {
  // Allow in-flight attempts from the just-ended visit to finish, but never
  // associate them with a later visit.
  if (!navigationId || attempt < 1) return;
  emit({
    event: "request_attempt",
    navigation_id: navigationId,
    attempt,
    duration_ms: Math.max(0, Math.round(durationMs)),
    outcome,
    status,
    request_id: requestId && SAFE_SERVER_REQUEST_ID.test(requestId) ? requestId : null,
  });
}

export function getBenchmarkPerformanceEvents(): BenchmarkPerformanceEvent[] {
  return events.map((event) => ({ ...event }));
}

export function clearBenchmarkPerformanceEvents(): void {
  events.splice(0, events.length);
  attemptCounts.clear();
  activeVisit = null;
  observedPath = null;
  visibleVisitId = null;
  endedVisitId = null;
  failedVisitId = null;
  cacheStateVisitId = null;
  capturedNavigationStart = null;
}

if (typeof window !== "undefined") {
  Object.defineProperty(window, "__ALTA_BENCHMARK_PERFORMANCE__", {
    configurable: true,
    value: { getEvents: getBenchmarkPerformanceEvents },
  });
}